import Button from "_shared/components/Button/Button";
import Tooltip from "_shared/components/Tooltip/Tooltip";
import plans from "_shared/constants/plans";
import { useSubscriptionsContext } from "account/manageSubscriptions/contexts/SubscriptionsContext";
import useSubChangeDetails from "account/manageSubscriptions/hooks/useSubChangeDetails";
import AddCardWithElements from "account/payment/components/AddCardModal/AddCardModal";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { BsInfoCircleFill } from "react-icons/bs";
import styles from "./ChangesPanel.module.scss";
import { Prompt } from "react-router-dom";

interface Props {
  noCard: boolean;
  getCardList: () => Promise<void>;
}
function ChangesPanel({ noCard, getCardList }: Props) {
  const { planChange, currentPlanIndex, selectedPlanIndex, reactivatePlan } =
    useSubscriptionsContext();

  const {
    addedSubscriptions,
    removedSubscriptions,
    proration,
    monthlyTotal,
    handleConfirm,
    hasChange,
  } = useSubChangeDetails();

  const [addingCard, setAddingCard] = useState(false);

  const ghost = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ghost,
    offset: ["start end", "end end"],
  });
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1]);
  const backgroundColor = useTransform(
    scrollYProgress,
    [0, 1],
    ["#222", "#171717"],
  );

  return (
    <>
      {/* prompt user if leaving page without saving changes */}
      <Prompt
        when={hasChange}
        message={`Are you sure you want to leave? Changes made to your subscription plan will not be saved.`}
      />
      <motion.aside
        layout
        style={{
          scale: hasChange ? scale : 1,
          position: hasChange ? "sticky" : "relative",
          backgroundColor,
        }}
        className={styles.container}
      >
        {currentPlanIndex < 0 ? (
          <div className={styles.noChanges}>
            <div>
              <button
                className={styles.reactivateButton}
                onClick={reactivatePlan}
              >
                Reactivate your plan to make changes.
              </button>
            </div>
          </div>
        ) : !hasChange ? (
          <div className={styles.noChanges}>
            You haven&apos;t made any changes to your plan
          </div>
        ) : (
          <>
            <div className={styles.changes}>
              <div className={styles.change}>
                <ul>
                  {/* plan changes */}
                  {currentPlanIndex !== selectedPlanIndex && (
                    <li
                      className={
                        planChange === "downgrade"
                          ? styles.downgrade
                          : styles.upgrade
                      }
                    >
                      {plans[currentPlanIndex].name} Plan →{" "}
                      {plans[selectedPlanIndex].name} Plan
                      <span className={styles.price}>
                        ${plans[selectedPlanIndex].priceMonthly}/mo
                      </span>
                    </li>
                  )}
                  {/* added subscriptions */}
                  {addedSubscriptions?.length! > 0 && (
                    <>
                      {addedSubscriptions
                        ?.filter((sub) => !sub.key.endsWith("plan"))
                        .map((sub) => (
                          <li key={sub.id} className={styles.upgrade}>
                            {sub.name}
                            <span className={styles.price}>
                              ${sub.monthly_daily_price}/mo
                            </span>
                          </li>
                        ))}
                    </>
                  )}
                  {/* removed subscriptions */}
                  {removedSubscriptions?.length! > 0 && (
                    <>
                      {removedSubscriptions
                        ?.filter((sub) => !sub.key.endsWith("plan"))
                        .map((sub) => (
                          <li key={sub.id} className={styles.downgrade}>
                            {sub.name}
                            <span className={styles.price}>
                              ${sub.monthly_daily_price}/mo
                            </span>
                          </li>
                        ))}
                    </>
                  )}
                </ul>
              </div>
            </div>
            <footer>
              {currentPlanIndex !== 0 ? (
                <Tooltip
                  maxWidth={500}
                  content="This represents the proportionate cost for the remaining days until your next billing date. After that, the regular full amount will apply."
                >
                  <div className={styles.message}>
                    {proration ? (
                      <>
                        <BsInfoCircleFill />
                        <div>
                          You will be charged a prorated amount of{" "}
                          <span className={styles.price}>${proration}</span>
                        </div>
                      </>
                    ) : null}
                  </div>
                </Tooltip>
              ) : (
                <Tooltip
                  maxWidth={500}
                  content="Your billing cycle will begin immediately since you are currently on the free plan."
                >
                  <div className={styles.message}>
                    <BsInfoCircleFill />
                    <div>
                      You will be charged{" "}
                      <span className={styles.price}>${monthlyTotal}</span>{" "}
                    </div>
                    monthly
                  </div>
                </Tooltip>
              )}
              <Button
                onClick={async () => {
                  if (!noCard && !addingCard) return setAddingCard(true);
                  await handleConfirm();
                }}
                disabled={!hasChange}
                className={styles.confirmButton}
              >
                Confirm changes
              </Button>
            </footer>
          </>
        )}
      </motion.aside>
      <div className={styles.ghost}>
        <div ref={ghost} />
      </div>
      {addingCard && (
        <AddCardWithElements
          open={addingCard}
          onSuccess={() => {
            setAddingCard(false);
            handleConfirm();
            getCardList();
          }}
          onClose={() => setAddingCard(false)}
        />
      )}
    </>
  );
}

export default ChangesPanel;
